import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';

/** How many pages to keep fetched beyond the last one in the grid. */
const PREFETCH_AHEAD = 2;

/** The band around the screen in which a view-more sentinel loads a page (px). */
const VIEW_MORE_MARGIN_TOP = 600;
const VIEW_MORE_MARGIN_BOTTOM = 2500;

/**
 * The Hoppy currency converter app rewrites every price added to the page.
 * Each rewrite reads innerText straight after the previous write, so every
 * price forces a layout of the whole page — and the page grows with every
 * page appended. On a phone that blocked the main thread for seconds per
 * page, and a fast scroll ran into blank screen that could not be painted.
 * In the store's own currency the rewrite changes nothing (Rs.8,490 becomes
 * Rs.8,490), so mark those prices done before they are added and the app
 * skips them. A shopper on another currency is converted as before.
 * @param {Iterable<Element>} elements
 */
function skipNoOpCurrencyConversion(elements) {
  let current;
  try {
    current = localStorage.getItem('currentCurrency');
  } catch {
    return;
  }
  // @ts-ignore - set by the app embed
  const store = window.futureBlink_storeCurrency || window.Shopify?.currency?.active;
  if (current && current !== store) return;

  for (const element of elements) {
    for (const price of element.querySelectorAll('.money, .price-item')) {
      price.setAttribute('data-processed', 'true');
    }
  }
}

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** A next page is being appended: a second trigger must not append it twice. */
  #renderingNext = false;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#prefetchAhead();
    this.#fetchPage('previous');
    this.#observeViewMore();

    // Listen for filter updates to clear cached pages
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          // Append the next page while it is still well below the screen (its
          // HTML is normally prefetched already), so a fast scroll finds
          // products rather than the end of the list. The top margin keeps a
          // marker that a fling carried just past the screen still counting.
          rootMargin: `${VIEW_MORE_MARGIN_TOP}px 0px ${VIEW_MORE_MARGIN_BOTTOM}px 0px`,
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const lastPage = this.#grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === 'next') {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    try {
      await this.#fetchSpecificPage(page.page, page.url);
    } catch {
      // A failed request must not leave a waiting render hanging forever.
    } finally {
      resolvePromise();
    }
  }

  /**
   * Keeps PREFETCH_AHEAD pages fetched past the last one shown. A page is
   * only 12 products — a hard fling covers that in well under a second, far
   * quicker than the section renders on the server — so one page of buffer
   * left the shopper waiting on a fresh request at the end of almost every
   * page. Requests already in flight are shared by the section renderer.
   */
  #prefetchAhead() {
    const next = this.#getPage('next');
    if (!next) return;

    for (let page = next.page; page < next.page + PREFETCH_AHEAD; page++) {
      if (!this.pages.has(page)) this.#fetchSpecificPage(page).catch(() => {});
    }

    // A merged search: the other query's first page early, in idle time. Its
    // size settles the count and where the list ends long before the shopper
    // scrolls that far.
    const alt = this.#altSearch;
    if (alt && !this.pages.has(alt.pages + 1)) {
      requestIdleCallback(() => this.#fetchSpecificPage(alt.pages + 1).catch(() => {}));
    }
  }

  /**
   * A search lists its own results, then the results of the same words quoted
   * (or unquoted, when they came quoted). The grid names that other query and
   * how many pages its own has; every page after those is the other query's:
   * primary + 1 is its page 1, and so on.
   * @returns {{ q: string, pages: number } | null}
   */
  get #altSearch() {
    const grid = this.#grid;
    const q = grid?.dataset.altQ;
    if (!q) return null;
    return { q, pages: Number(grid.dataset.primaryPages) || 0 };
  }

  /**
   * One page of the other query, as this list will show it: renumbered to its
   * place after the primary pages, reduced to the cards. The first one also
   * tells us how many pages there really are and roughly how many products.
   * @param {number} pageNumber
   * @param {string} html
   * @param {{ q: string, pages: number }} alt
   * @returns {string}
   */
  #adoptAltPage(pageNumber, html, alt) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const altGrid = doc.querySelector('[ref="grid"]');
    const cards = altGrid ? [...altGrid.querySelectorAll(':scope > [ref="cards[]"]')] : [];
    for (const card of cards) card.setAttribute('data-page', String(pageNumber));

    const grid = this.#grid;
    if (pageNumber === alt.pages + 1 && grid) {
      // Its own pages are its data-primary-pages: its data-last-page counts
      // one more, for the query this page started from.
      const altPages = altGrid ? Number(altGrid.getAttribute('data-primary-pages')) || 0 : 0;
      grid.dataset.lastPage = String(alt.pages + altPages);

      // Exact matches are nearly always among the loose ones, so the larger of
      // the two counts is the closest guess; ia-infinite-cap makes it exact
      // once the last page is in. Setting data-total also has it re-check,
      // which it needs now the end of the list has moved.
      const altTotal = altGrid ? Number(altGrid.getAttribute('data-total')) || 0 : 0;
      const total = Math.max(Number(grid.dataset.total) || 0, altTotal);
      this.querySelector('ia-infinite-cap')?.setAttribute('data-total', String(total));
      const count = this.querySelector('.ia-plp__count strong');
      if (count) count.textContent = String(total);
    }

    return `<ul ref="grid">${cards.map((card) => card.outerHTML).join('')}</ul>`;
  }

  /**
   * Hides the cards of a merged search's later page that the list already
   * shows. Hidden rather than dropped: a page with nothing new must still
   * count as rendered, or the same page would be asked for again and again.
   * @param {HTMLElement} grid
   * @param {NodeListOf<Element>} elements
   * @param {number} pageNumber
   */
  #hideRepeats(grid, elements, pageNumber) {
    const listed = new Set();
    for (const card of grid.querySelectorAll(':scope > [data-product-id]')) {
      listed.add(/** @type {HTMLElement} */ (card).dataset.productId);
    }
    for (const element of elements) {
      const card = /** @type {HTMLElement} */ (element);
      if (!listed.has(card.dataset.productId)) {
        listed.add(card.dataset.productId);
        continue;
      }
      card.hidden = true;
      card.removeAttribute('id');
    }
    // ia.js keeps this page's HTML for the way back from a product: it must
    // put the repeats back hidden too.
    this.pages.set(pageNumber, `<ul ref="grid">${[...elements].map((element) => element.outerHTML).join('')}</ul>`);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set('page', pageNumber.toString());
      newUrl.hash = '';
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;

    const alt = this.#altSearch;
    if (alt && pageNumber > alt.pages) {
      const altUrl = new URL(pageInfo.url);
      altUrl.searchParams.set('q', alt.q);
      altUrl.searchParams.set('page', String(pageNumber - alt.pages));
      const filters = location.search;
      const altContent = await sectionRenderer.getSectionHTML(this.sectionId, true, altUrl);
      // A filter changed while it was in flight: these are the old results.
      if (location.search !== filters) return;
      this.pages.set(pageNumber, this.#adoptAltPage(pageNumber, altContent, alt));
      return;
    }

    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, true, pageInfo.url);
    this.pages.set(pageNumber, pageContent);
  }

  async #renderNextPage() {
    if (this.#renderingNext) return;
    this.#renderingNext = true;

    let appended = false;
    try {
      appended = await this.#appendNextPage();
    } finally {
      this.#renderingNext = false;
    }

    if (appended) this.#continueIfStillInView();
  }

  /**
   * The observer only reports the sentinel when it ENTERS the loading band.
   * A page appended during a fast scroll can leave the sentinel still inside
   * the band, so no new entry is ever reported and loading stalled at the
   * skeleton cards until the shopper scrolled away and back. Keep going
   * while it is still in the band — unless ia-infinite-cap has paused it.
   */
  #continueIfStillInView() {
    requestAnimationFrame(() => {
      const { viewMoreNext } = this.refs;
      if (!viewMoreNext?.isConnected || viewMoreNext.hidden) return;

      const box = viewMoreNext.getBoundingClientRect();
      if (box.top < window.innerHeight + VIEW_MORE_MARGIN_BOTTOM && box.bottom > -VIEW_MORE_MARGIN_TOP) {
        this.#renderNextPage();
      }
    });
  }

  /** @returns {Promise<boolean>} Whether a page was appended */
  async #appendNextPage() {
    const grid = this.#grid;

    if (!grid) return false;

    const nextPage = this.#getPage('next');

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;
    // Never the same page twice, whatever triggered this.
    if (this.#renderedPages().has(nextPage.page)) return false;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('next');

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) return false;
    }

    // The wait above can overlap a filter change or another append.
    if (this.#renderedPages().has(nextPage.page)) return false;

    const alt = this.#altSearch;
    if (alt && nextPage.page > alt.pages) this.#hideRepeats(grid, nextPageItemElements, nextPage.page);

    skipNoOpCurrencyConversion(nextPageItemElements);
    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    // Deep-linking the scrolled-to page rewrites the address bar as the
    // shopper scrolls and stacks history entries nobody asked for — and it
    // leaves ?page=N on the URL, which the next filter request then honours
    // and jumps to the middle of the results. Opt in with track-page-in-url.
    if (this.hasAttribute('track-page-in-url')) history.pushState('', '', nextPage.url.toString());

    // Straight away, not on idle: idle time never comes during a fast
    // scroll, which is exactly when the next pages are needed.
    this.#prefetchAhead();

    return true;
  }

  async #renderPreviousPage() {
    const grid = this.#grid;

    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;
    if (this.#renderedPages().has(previousPage.page)) return;

    let previousPageItemElements = this.#getGridForPage(previousPage.page);
    if (!previousPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolvePreviousPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage('previous');

      await promise;
      previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) return;
    }

    if (this.#renderedPages().has(previousPage.page)) return;

    // Store the current scroll position and height of the first element
    const scrollTop = window.scrollY;
    const firstElement = grid.firstElementChild;
    const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

    // Prepend the new elements
    skipNoOpCurrencyConversion(previousPageItemElements);
    grid.prepend(...previousPageItemElements);

    this.#aspectRatioHelper?.processNewElements();

    if (this.hasAttribute('track-page-in-url')) history.pushState('', '', previousPage.url.toString());

    // Calculate and adjust scroll position to maintain the same view
    if (firstElement) {
      const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
      const heightDiff = newHeight - oldHeight;
      window.scrollTo({
        top: scrollTop + heightDiff,
        behavior: 'instant',
      });
    }

    requestIdleCallback(() => {
      this.#fetchPage('previous');
    });
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const isPrevious = type === 'previous';
    const pages = this.#renderedPages();

    if (!pages.size) return;

    // Read from the grid itself, not from refs.cards: the refs are refreshed
    // by a mutation observer that is only attached once the browser is idle,
    // and idle time never comes during a fast scroll. Stale refs kept naming
    // the first page as the last one shown, so page 2 was fetched and
    // appended over and over ("Showing 360 of 86").
    const currentCardPage = isPrevious ? Math.min(...pages) : Math.max(...pages);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /** The live grid: refs.grid can be stale for the same reason as refs.cards. */
  get #grid() {
    return this.querySelector('[ref="grid"]') ?? this.refs.grid;
  }

  /**
   * Page numbers of the cards currently in the grid, read live.
   * @returns {Set<number>}
   */
  #renderedPages() {
    const pages = new Set();
    const grid = this.#grid;
    if (!grid) return pages;
    for (const card of grid.querySelectorAll(':scope > [ref="cards[]"][data-page]')) {
      const page = Number(/** @type {HTMLElement} */ (card).dataset.page);
      if (page > 0) pages.add(page);
    }
    return pages;
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage value to detect when it changes
    const currentLastPage = this.#grid?.dataset.lastPage;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      // Check if data-last-page changed
      const newLastPage = this.#grid?.dataset.lastPage;

      if (newLastPage !== currentLastPage) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        this.#observeViewMore();

        // Fetch the next pages
        this.#prefetchAhead();
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ['data-last-page'],
        childList: true, // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
